import { AutoscaleInfo, IChartApi, Logical, Time } from 'lightweight-charts'
import { BitmapPositionLength, DrawingTypes } from './drawing.interface'
import { drawingClassMap } from './drawing.constants'

/**
 * Determines the bitmap position and length for a dimension of a shape to be drawn.
 * @param position1Media - media coordinate for the first point
 * @param position2Media - media coordinate for the second point
 * @param pixelRatio - pixel ratio for the corresponding axis (vertical or horizontal)
 * @returns Position of of the start point and length dimension.
 */
export function positionsBox(
    position1Media: number,
    position2Media: number,
    pixelRatio: number
): BitmapPositionLength {
    const scaledPosition1 = Math.round(pixelRatio * position1Media)
    const scaledPosition2 = Math.round(pixelRatio * position2Media)
    return {
        position: Math.min(scaledPosition1, scaledPosition2),
        length: Math.abs(scaledPosition2 - scaledPosition1) + 1
    }
}

export function scaledCoordinate(coordinate: number, pixelRatio: number) {
    return Math.round(coordinate * pixelRatio)
}

export function determineChartX(
    chart: IChartApi,
    chartBounds: DOMRect,
    mouseX: number
): number | null {
    const x = mouseX - chartBounds.left - chart.priceScale('left').width()
    if (x < 0 || x > chart.timeScale().width()) return null
    return x
}

export function determineChartY(
    chart: IChartApi,
    chartBounds: DOMRect,
    mouseY: number
): number | null {
    const y = mouseY - chartBounds.top
    if (y < 0 || y > chartBounds.height) return null
    const ts = chart.timeScale().height()
    const clippedMax = chartBounds.height - ts
    const maxY = clippedMax >= chartBounds.height * 0.5 ? clippedMax : chartBounds.height
    if (y > maxY) return null
    return y
}

export function getDrawingClass(type: DrawingTypes) {
    return drawingClassMap[type]
}

export function getDistanceFromLine(x: number, y: number, slope: number, intercept: number) {
    return Math.abs(y * (-1) + slope * x + intercept) / Math.sqrt(slope * slope + 1)
}

export function getDistanceBetweenPoints(x1: number, x2: number, y1: number, y2: number) {
    return Math.sqrt(Math.pow(x2 - x1, 2) + Math.pow(y2 - y1, 2))
}

export function getDistanceFromSegment(
    px: number,
    py: number,
    x1: number,
    y1: number,
    x2: number,
    y2: number,
) {
    const dx = x2 - x1
    const dy = y2 - y1
    if (dx === 0 && dy === 0) {
        return getDistanceBetweenPoints(px, x1, py, y1)
    }
    const t = Math.max(0, Math.min(1, ((px - x1) * dx + (py - y1) * dy) / (dx * dx + dy * dy)))
    const projX = x1 + t * dx
    const projY = y1 + t * dy
    return getDistanceBetweenPoints(px, projX, py, projY)
}

export function calculateTriangleArea(x1: number, y1: number, x2: number, y2: number, x3: number, y3: number) {
    return Math.abs((x1 * (y2 - y3) + x2 * (y3 - y1) + x3 * (y1 - y2)) / 2.0)
}

export function isSegmentIntersecting(p1x: number, p1y: number, p2x: number, p2y: number, slope: number, intercept: number) {
    const d1 = p1y - slope * p1x - intercept
    const d2 = p2y - slope * p2x - intercept

    return d1 * d2 < 0
}

export function buildAutoscaleInfoFromPrices(prices: number[]): AutoscaleInfo | null {
    if (!prices.length) return null

    let minValue = prices[0]
    let maxValue = prices[0]
    for (let i = 1; i < prices.length; i++) {
        const price = prices[i]
        if (!Number.isFinite(price)) continue
        if (price < minValue) minValue = price
        if (price > maxValue) maxValue = price
    }

    if (!Number.isFinite(minValue) || !Number.isFinite(maxValue)) return null
    return {
        priceRange: {
            minValue,
            maxValue,
        },
    }
}

export function isTimeRangeOverlappingVisibleLogicalRange(
    times: Array<Time | number>,
    timeToLogical: (time: Time) => number | null,
    startTimePoint: Logical,
    endTimePoint: Logical,
): boolean {
    const logicals = times
        .map((time) => timeToLogical(time as Time))
        .filter((logical): logical is number => logical != null)

    if (!logicals.length) return false

    const rangeStart = Math.min(...logicals)
    const rangeEnd = Math.max(...logicals)
    return rangeStart <= endTimePoint && rangeEnd >= startTimePoint
}